import React from "react";
import { Button, Spinner } from "react-bootstrap";
import Icon from "@mdi/react";
import {
  mdiRefresh,
  mdiInformationOutline,
  mdiCalendarToday,
  mdiMapMarkerOutline,
} from "@mdi/js";
import { PartialLoadingSpinner } from "app/components/LoadingSpinner";

/**
 * If the body is plain text (no <p> or <br> tags), convert \n to <br>.
 * If it's already HTML with paragraphs, return as-is.
 * Also converts report link variables into clickable hyperlinks.
 */
const formatPreviewBody = (body) => {
  if (!body) return "";
  let result = body;

  // Convert plain text \n to <br> if no block-level HTML tags
  const hasBlockTags = /<(p|div|br|ul|ol|li)[\s>]/i.test(result);
  if (!hasBlockTags) {
    result = result.replace(/\n/g, "<br>");
  }

  // Convert report link variable spans to clickable links
  result = result.replace(
    /<span class="variable">((https?:\/\/[^<]+))<\/span>/g,
    '<a href="$1" class="variable preview-link" target="_blank" rel="noopener noreferrer">$1</a>'
  );

  return result;
};

const PreviewPanel = ({
  preview,
  previewFetchStatus,
  refreshPreviewStatus,
  onRefresh,
  senderInfo,
  templateInfo,
}) => {
  const isRefreshing = refreshPreviewStatus === "loading";

  return (
    <div className="preview-panel">
      <div className="preview-header">
        <h5 className="preview-title">Preview</h5>
        <Button
          variant="outline-primary"
          size="sm"
          className="preview-refresh-btn"
          onClick={onRefresh}
          disabled={isRefreshing}
        >
          {isRefreshing ? (
            <Spinner size="sm" animation="border" />
          ) : (
            <Icon path={mdiRefresh} size={0.67} />
          )}
          Refresh
        </Button>
      </div>

      {previewFetchStatus === "loading" ? (
        <div className="preview-loading">
          <PartialLoadingSpinner />
        </div>
      ) : preview ? (
        <div className="preview-email">
          {/* Subject + From/To/CC */}
          <div className="preview-subject-section">
            <div
              className="preview-subject"
              dangerouslySetInnerHTML={{
                __html: preview.subject || "",
              }}
            />
            <div className="preview-meta">
              {senderInfo?.name && (
                <div className="preview-meta-row">
                  <span className="meta-label">From:</span>
                  <span className="meta-value">
                    <span className="meta-name">{senderInfo.name}</span>
                    {senderInfo.email && (
                      <span className="meta-email">
                        {" "}&lt;{senderInfo.email}&gt;
                      </span>
                    )}
                  </span>
                </div>
              )}
              <div className="preview-meta-row">
                <span className="meta-label">To:</span>
                <span className="meta-value">
                  <span className="meta-name variable">John Doe</span>
                  <span className="meta-email">
                    {" "}&lt;john.doe@example.com&gt;
                  </span>
                </span>
              </div>
              {templateInfo?.cc_agent && (
                <div className="preview-meta-row">
                  <span className="meta-label">CC:</span>
                  <span className="meta-value meta-cc">Property Agent</span>
                </div>
              )}
            </div>
          </div>

          {/* Body */}
          <div
            className="preview-body"
            dangerouslySetInnerHTML={{
              __html: formatPreviewBody(preview.body || ""),
            }}
          />

          {/* Inspection Details */}
          <div className="preview-inspection-details">
            <div className="preview-detail-row">
              <Icon
                path={mdiCalendarToday}
                size={1}
                className="preview-detail-icon"
              />
              <div className="preview-detail-text">
                <span className="preview-detail-primary">Jan 31, 2026</span>
                <span className="preview-detail-secondary">10:00 AM</span>
              </div>
            </div>
            <div className="preview-detail-row">
              <Icon
                path={mdiMapMarkerOutline}
                size={1}
                className="preview-detail-icon"
              />
              <div className="preview-detail-text">
                <span className="preview-detail-primary">
                  123 Maple Avenue
                </span>
                <span className="preview-detail-secondary">
                  Springfield, IL 62704
                </span>
              </div>
            </div>
          </div>

          {/* Company Footer */}
          <div className="preview-company-footer">
            <span className="preview-company-label">INSPECTION SERVICES</span>
            <span className="preview-company-name">
              {senderInfo?.companyName || "Your Company"}
            </span>
          </div>
        </div>
      ) : (
        <div className="preview-empty">
          <p>No preview available.</p>
        </div>
      )}

      <div className="preview-info-text">
        <Icon path={mdiInformationOutline} size={0.67} />
        <span>The actual email will be sent with real inspection data.</span>
      </div>
    </div>
  );
};

export default PreviewPanel;
